import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import ExamCards from './ExamCards';
import { fetchTests, setCurrentPage } from '../../actions/testActions';
import { Spinner, Pagination, Form, InputGroup, Button, Modal, Row, Col } from 'react-bootstrap';
import { FiSearch, FiFilter } from 'react-icons/fi';
import SEO from '../common/SEO';

function AllExams() {
    const {
        tests,
        loading,
        pagination: { currentPage, totalPages, limit },
        filters: { category }
    } = useSelector(state => state.tests);

    const studentProfileData = useSelector(state => state.dashboard.userData);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState('');
    const [showFiltersModal, setShowFiltersModal] = useState(false);

    // Debounced search fetch
    const debouncedSearchFetch = useCallback(
        debounce((page, limit, category, query) => {
            let paginated = false
            dispatch(fetchTests(page, limit, category === 'All' ? null : category, query, paginated));
        }, 500),
        [dispatch]
    );

    // Fetch tests on mount and when category or page changes (not for search)
    useEffect(() => {
        dispatch(fetchTests(currentPage, limit, category === 'All' ? null : category, ''));
        setSearchQuery('');
        // Cancel any pending debounced search
        debouncedSearchFetch.cancel();
    }, [currentPage]);

    // Fetch tests when searchQuery changes (debounced)
    useEffect(() => {
        if (searchQuery.trim() !== '') {
            debouncedSearchFetch(currentPage, limit, category, searchQuery);
        }
        return () => debouncedSearchFetch.cancel();
    }, [searchQuery, currentPage, limit, category, debouncedSearchFetch]);

    const handlePageChange = (page) => {
        if (page !== currentPage) {
            dispatch(setCurrentPage(page));
        }
    };

    const handleCategoryChange = (newCategory) => {
        dispatch(setCurrentPage(1));
        let paginated = false;
        dispatch(fetchTests(1, limit, newCategory === 'All' ? null : newCategory, '', paginated));
        setSearchQuery('');
    };

    const handleSearch = (e) => {
        e.preventDefault();
        dispatch(setCurrentPage(1));
        debouncedSearchFetch(1, limit, category, searchQuery);
    };

    const handleAttemptTest = (test) => {
        navigate(`/exam-terms-conditions`, { state: { test, studentProfileData } });
    };

    const resetFilters = () => {
        setSearchQuery('');
        handleCategoryChange('All');
    };

    // Generate pagination items
    const renderPaginationItems = () => {
        let items = [];
        const maxVisiblePages = 5;
        let startPage, endPage;

        if (totalPages <= maxVisiblePages) {
            startPage = 1;
            endPage = totalPages;
        } else {
            const maxVisibleBeforeCurrent = Math.floor(maxVisiblePages / 2);
            const maxVisibleAfterCurrent = Math.ceil(maxVisiblePages / 2) - 1;

            if (currentPage <= maxVisibleBeforeCurrent) {
                startPage = 1;
                endPage = maxVisiblePages;
            } else if (currentPage + maxVisibleAfterCurrent >= totalPages) {
                startPage = totalPages - maxVisiblePages + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - maxVisibleBeforeCurrent;
                endPage = currentPage + maxVisibleAfterCurrent;
            }
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        return items;
    };


    // Generate SEO data based on current state and test data
    const generateSeoData = () => {
        const testCount = tests?.length || 0;
        const baseTitle = "All Tests & Exam Series - My Exam World";
        const baseDescription = "Access comprehensive test series and practice exams across all subjects. Prepare for competitive exams, academic tests, and skill assessments with our extensive collection of online tests.";
        const baseKeywords = "all tests, exam series, online tests, practice exams, test preparation, competitive exams, skill assessment, mock tests, online assessment";

        // Extract test titles for keywords
        const testTitles = tests?.slice(0, 10).map(test => test.title) || [];

        // Dynamic title and description based on filters
        let title = baseTitle;
        let description = baseDescription;
        let keywords = baseKeywords;

        if (category && category !== 'All') {
            title = `All ${category} Tests - Complete Test Series | My Exam World`;
            description = `Comprehensive collection of ${category.toLowerCase()} tests and exam series. Practice with ${testCount}+ ${category.toLowerCase()} tests to improve your knowledge and exam performance.`;
            keywords = `${category.toLowerCase()} tests, ${category.toLowerCase()} exam series, ${category.toLowerCase()} practice tests, ${testTitles.join(', ')}, ${keywords}`;
        }

        if (searchQuery) {
            title = `Search Results: ${searchQuery} - All Tests | My Exam World`;
            description = `Find all tests related to "${searchQuery}". Explore ${testCount} practice exams and test series covering ${searchQuery} topics and concepts.`;
            keywords = `${searchQuery} tests, ${searchQuery} exams, ${searchQuery} practice tests, ${testTitles.join(', ')}, ${keywords}`;
        }

        // Add test titles to keywords (limited to first 10 to avoid overly long meta tags)
        if (testTitles.length > 0) {
            keywords = `${keywords}, ${testTitles.join(', ')}`;
        }

        return {
            title,
            description: description.substring(0, 160), // Ensure description is not too long
            keywords: keywords.substring(0, 255), // Limit keywords length
            url: "/alltest"
        };
    };

    const seoData = generateSeoData();

    return (
        <div className='container mt-3 exam-container'>
            <SEO
                title={seoData.title}
                description={seoData.description}
                keywords={seoData.keywords}
                url={seoData.url}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "name": "Live Tests & Practice Series",
                    "numberOfItems": tests?.length || 0,
                    "itemListElement": tests?.slice(0, 10).map((test, index) => ({
                        "@type": "ListItem",
                        "position": index + 1,
                        "name": test.testName || test.title,
                        "url": `https://www.myexamworld.com/test/${test.testName ? test.testName.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'exam'}/${test._id || test.id}`
                    })) || []
                }}
            />
            {/* Title Row */}
            <Row className="mb-3 align-items-center">
                <Col xs={8} md={10}>
                    <h2 className="mb-0">Available Tests</h2>
                </Col>
                <Col xs={4} md={2} className="text-end">
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowFiltersModal(true)}
                        className="d-md-none mobile-filter-button"
                    >
                        <FiFilter size={20} />
                    </Button>
                </Col>
            </Row>

            {/* Desktop Search and Filter */}
            <Form onSubmit={handleSearch} className="d-none d-md-block mb-4">
                <Row className="align-items-end">
                    <Col md={8}>
                        <InputGroup>
                            <Form.Control
                                type="search"
                                placeholder="Search tests by name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Button  className='button-design' type="submit">
                                <FiSearch />
                            </Button>
                        </InputGroup>
                    </Col>
                    <Col md={4} className="d-flex justify-content-end">
                        <InputGroup>
                            <InputGroup.Text className="bg-white">Category:</InputGroup.Text>
                            <Form.Select
                                value={category}
                                onChange={(e) => handleCategoryChange(e.target.value)}
                            >
                                <option value="All">All Categories</option>
                                <option value="Quantitative Aptitude">Quantitative Aptitude</option>
                                <option value="History">History</option>
                                <option value="Logical reasoning">Logical reasoning</option>
                                <option value="Computer">Computer</option>
                                <option value="Economics">Economics</option>
                                <option value="Geography">Geography</option>
                                <option value="Mathematics">Mathematics</option>
                                <option value="Current affairs">Current affairs</option>
                                <option value="General knowledge">General knowledge</option>
                            </Form.Select>
                        </InputGroup>
                    </Col>
                </Row>
            </Form>

            {/* Mobile Search */}
            <Form onSubmit={handleSearch} className="d-block d-md-none mb-3">
                <InputGroup>
                    <Form.Control
                        type="search"
                        placeholder="Search tests..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Button className='button-design' type="submit">
                        <FiSearch />
                    </Button>
                </InputGroup>
            </Form>

            {/* Filters Modal - For mobile view */}
            <Modal
                show={showFiltersModal}
                onHide={() => setShowFiltersModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Filter Tests</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <Form.Select
                            value={category}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                            size="lg"
                        >
                            <option value="All">All Categories</option>
                            <option value="Quantitative Aptitude">Quantitative Aptitude</option>
                            <option value="History">History</option>
                            <option value="Logical reasoning">Logical reasoning</option>
                            <option value="Computer">Computer</option>
                            <option value="Economics">Economics</option>
                            <option value="Geography">Geography</option>
                            <option value="Mathematics">Mathematics</option>
                            <option value="Current affairs">Current affairs</option>
                            <option value="General knowledge">General knowledge</option>
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowFiltersModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={() => setShowFiltersModal(false)}>
                        Apply Filters
                    </Button>
                    <Button variant="outline-danger" onClick={resetFilters}>
                        Reset Filters
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Content Section */}
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '30vh' }}>
                    <Spinner animation="border" className='main-color' />
                </div>
            ) : tests?.length === 0 ? (
                <div className="text-center py-5">
                    <h5 className="text-muted">No tests found matching your criteria</h5>
                    <Button variant="primary" onClick={resetFilters} className="mt-3">
                        Reset Filters
                    </Button>
                </div>
            ) : (
                <>
                    <div className="row main-Conatiner">
                        {tests?.map((test, index) => (
                            <ExamCards key={test._id} index={index} test={test} onAttemptTest={handleAttemptTest} />
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <Pagination className='button-design-pagination'>
                                <Pagination.First
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                />

                                {renderPaginationItems()}

                                <Pagination.Next
                                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default AllExams;